// Utilities to print a clean work card per booking, or a full-day overview.
// Uses the browser print dialog (Skriv ut -> Lagre som PDF) — no extra integration.

const esc = (v) => {
  if (v === null || v === undefined) return '';
  return String(v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
};

const STATUS_LABELS = {
  confirmed: 'Bekreftet',
  completed: 'Fullført',
  cancelled: 'Kansellert',
  pending: 'Venter',
  no_show: 'Ikke møtt',
};

const VEHICLE_LABELS = {
  tungbil: 'Tungbil',
  lastebil: 'Lastebil',
  buss: 'Buss',
  tilhenger: 'Tilhenger',
  henger: 'Tilhenger',
  personbil: 'Personbil',
  bil: 'Personbil',
  varebil: 'Varebil',
};

const labelStatus = (s) => STATUS_LABELS[s] || (s || '');
const labelVehicle = (v) => VEHICLE_LABELS[(v || '').toLowerCase()] || (v || '');

const companyBlock = (company) => {
  const name = esc(company?.company_name || company?.name || 'Sand-tec AS');
  const a = company?.address || {};
  const addr = [a.street, [a.postal_code, a.city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .map(esc)
    .join(', ');
  return `
    <div class="company">
      <div class="company-name">${name}</div>
      ${addr ? `<div class="company-addr">${addr}</div>` : ''}
    </div>`;
};

const baseStyles = `
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111827; margin: 0; padding: 28px; }
  .top { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #dc2626; padding-bottom: 14px; margin-bottom: 22px; }
  .company-name { font-size: 20px; font-weight: 700; }
  .company-addr { font-size: 12px; color: #6b7280; margin-top: 2px; }
  .doc-title { font-size: 22px; font-weight: 700; text-align: right; }
  .doc-sub { font-size: 12px; color: #6b7280; text-align: right; margin-top: 2px; }
  .badge { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 12px; font-weight: 700; border: 1px solid #d1d5db; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 28px; margin: 18px 0; }
  .field { padding: 8px 0; border-bottom: 1px solid #e5e7eb; }
  .field .lbl { font-size: 11px; text-transform: uppercase; letter-spacing: .04em; color: #6b7280; }
  .field .val { font-size: 15px; font-weight: 600; margin-top: 2px; }
  .reg { font-family: 'Courier New', monospace; letter-spacing: 1px; }
  .notes { margin-top: 16px; border: 1px solid #e5e7eb; border-radius: 8px; padding: 14px; }
  .notes .lbl { font-size: 11px; text-transform: uppercase; color: #6b7280; margin-bottom: 6px; }
  .notes .val { font-size: 15px; white-space: pre-wrap; min-height: 40px; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; }
  th { text-align: left; font-size: 11px; text-transform: uppercase; color: #6b7280; border-bottom: 2px solid #111827; padding: 8px 6px; }
  td { font-size: 13px; padding: 9px 6px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
  .foot { margin-top: 28px; font-size: 11px; color: #9ca3af; text-align: center; }
  @media print { body { padding: 12mm; } .work-card { page-break-after: always; } .work-card:last-child { page-break-after: auto; } }
`;

const openAndPrint = (title, bodyHtml) => {
  const win = window.open('', '_blank', 'width=900,height=1000');
  if (!win) {
    alert('Kunne ikke åpne utskriftsvindu. Tillat pop-ups for denne siden.');
    return;
  }
  win.document.write(`<!DOCTYPE html><html lang="no"><head><meta charset="utf-8"><title>${esc(title)}</title><style>${baseStyles}</style></head><body>${bodyHtml}<div class="foot">Generert ${new Date().toLocaleString('no-NO')} • Sand-tec AS</div></body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => {
    win.print();
  }, 350);
};

const workCardHtml = (booking, company) => `
  <div class="work-card">
    <div class="top">
      ${companyBlock(company)}
      <div>
        <div class="doc-title">Arbeidskort</div>
        <div class="doc-sub">#${esc(booking.booking_number)}</div>
        <div class="doc-sub"><span class="badge">${esc(labelStatus(booking.status))}</span></div>
      </div>
    </div>
    <div class="grid">
      <div class="field"><div class="lbl">Kunde</div><div class="val">${esc(booking.customer_name)}</div></div>
      <div class="field"><div class="lbl">Telefon</div><div class="val">${esc(booking.customer_phone)}</div></div>
      <div class="field"><div class="lbl">E-post</div><div class="val">${esc(booking.customer_email)}</div></div>
      <div class="field"><div class="lbl">Kjøretøy</div><div class="val">${esc(labelVehicle(booking.vehicle_type))}</div></div>
      <div class="field"><div class="lbl">Kjennemerke</div><div class="val reg">${esc((booking.reg_number || '').toUpperCase()) || '—'}</div></div>
      <div class="field"><div class="lbl">Dato</div><div class="val">${esc(booking.date)}</div></div>
      <div class="field"><div class="lbl">Tidspunkt</div><div class="val">${esc(booking.time_slot)}</div></div>
      <div class="field"><div class="lbl">Plass</div><div class="val">#${esc(booking.slot_number)}</div></div>
    </div>
    <div class="notes">
      <div class="lbl">Hva skal gjøres med bilen (notat)</div>
      <div class="val">${esc(booking.notes) || '—'}</div>
    </div>
  </div>`;

export const printBookingWorkCard = (booking, company) => {
  openAndPrint(`Arbeidskort ${booking.booking_number}`, workCardHtml(booking, company));
};

export const printDaySheet = (dateLabel, bookings, company) => {
  const rows = (bookings || [])
    .slice()
    .sort((a, b) => (a.time_slot || '').localeCompare(b.time_slot || ''))
    .map(
      (b) => `
      <tr>
        <td><strong>${esc(b.time_slot)}</strong><br><span style="color:#6b7280;">Plass #${esc(b.slot_number)}</span></td>
        <td>${esc(b.customer_name)}<br><span style="color:#6b7280;">${esc(b.customer_phone)}</span></td>
        <td>${esc(labelVehicle(b.vehicle_type))}<br><span class="reg">${esc((b.reg_number || '').toUpperCase()) || '—'}</span></td>
        <td>${esc(labelStatus(b.status))}</td>
        <td>${esc(b.notes) || '—'}</td>
      </tr>`
    )
    .join('');

  const body = `
    <div class="top">
      ${companyBlock(company)}
      <div>
        <div class="doc-title">Dagsoversikt</div>
        <div class="doc-sub">${esc(dateLabel)}</div>
        <div class="doc-sub">${(bookings || []).length} booking(er)</div>
      </div>
    </div>
    ${
      (bookings || []).length
        ? `<table>
            <thead><tr><th>Tid</th><th>Kunde</th><th>Kjøretøy / kjennemerke</th><th>Status</th><th>Hva skal gjøres (notat)</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>`
        : `<p style="color:#6b7280;">Ingen bookinger denne dagen.</p>`
    }`;

  openAndPrint(`Dagsoversikt ${dateLabel}`, body);
};
